import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Moon, Sun, Wifi, WifiOff } from 'lucide-react';
import { useNavigationStore } from '@genie-info/stores/navigationStore';
import { useSettingsStore } from '@genie-info/stores/settingsStore';
import { useOfflineStore } from '@genie-info/stores/offlineStore';
import { ContentViewer } from '@genie-info/content/ContentViewer';
import { useTranslationStore } from '@genie-info/stores/translationStore';

const OfflineCachePanel: React.FC = () => {
  const { sections } = useNavigationStore();
  const { moduleStatus, isCaching, isOnline, isSupported, cacheSection, cacheAllSections } =
    useOfflineStore();

  if (!isSupported) {
    return (
      <p className="text-xs text-gray-500">
        Este navegador no soporta almacenamiento offline avanzado.
      </p>
    );
  }

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800/60 p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-200">
        {isOnline ? <Wifi className="h-4 w-4 text-green-400" /> : <WifiOff className="h-4 w-4 text-amber-400" />}
        Contenido disponible offline
      </div>
      <p className="mb-3 text-xs text-gray-400">
        La PWA precachea la app completa. Aquí puedes forzar la caché de secciones del atlas.
      </p>
      <button
        type="button"
        disabled={!!isCaching || !isOnline}
        onClick={() => cacheAllSections(sections.map((section) => section.id))}
        className="mb-3 inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
      >
        <Download className="h-3.5 w-3.5" />
        {isCaching ? `Cacheando ${isCaching}...` : 'Cachear todas las secciones'}
      </button>
      <ul className="space-y-2">
        {sections.map((section) => {
          const status = moduleStatus[section.id];
          return (
            <li key={section.id} className="flex items-center justify-between gap-2 text-xs">
              <span className="text-gray-300">{section.title}</span>
              <div className="flex items-center gap-2">
                {status?.cached ? (
                  <span className="text-green-400">Cacheada</span>
                ) : (
                  <button
                    type="button"
                    disabled={!!isCaching || !isOnline}
                    onClick={() => cacheSection(section.id)}
                    className="text-blue-400 hover:text-blue-300 disabled:opacity-50"
                  >
                    Cachear
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

interface AtlasShellProps {
  section?: string;
  subsection?: string;
}

const AtlasShell: React.FC<AtlasShellProps> = ({ section, subsection }) => {
  const { sections, activeSection, activeSubsection, navigateTo } = useNavigationStore();
  const { isDarkMode, toggleDarkMode } = useSettingsStore();
  const { t } = useTranslationStore();
  const initializeOffline = useOfflineStore((state) => state.initialize);

  useEffect(() => {
    initializeOffline();
  }, [initializeOffline]);

  useEffect(() => {
    if (section) {
      navigateTo(section, subsection ?? null);
    }
  }, [section, subsection, navigateTo]);

  const shellBg = isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-100 text-gray-900';
  const panelBg = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';

  return (
    <div className={`min-h-screen ${shellBg}`}>
      <header className={`border-b ${isDarkMode ? 'border-gray-800 bg-gray-900/95' : 'border-gray-200 bg-white/95'} sticky top-0 z-20 backdrop-blur`}>
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-4">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white">
              <ArrowLeft className="h-4 w-4" />
              Inicio
            </Link>
            <h1 className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
              Atlas ENMG
            </h1>
          </div>
          <button
            type="button"
            onClick={toggleDarkMode}
            className="rounded-md border border-gray-700 p-2 text-gray-300 hover:bg-gray-800"
            aria-label="Alternar tema"
          >
            {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
        </div>
      </header>

      <div className="mx-auto grid max-w-7xl gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[280px_1fr]">
        <aside className={`rounded-lg border p-4 ${panelBg}`}>
          <nav className="space-y-4">
            {sections.map((navSection) => (
              <div key={navSection.id}>
                <button
                  type="button"
                  onClick={() => navigateTo(navSection.id, navSection.subsections?.[0]?.id ?? null)}
                  className={`mb-2 w-full text-left text-sm font-semibold ${
                    activeSection === navSection.id ? 'text-blue-400' : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {navSection.title}
                </button>
                {navSection.subsections && activeSection === navSection.id && (
                  <ul className="ml-3 space-y-1 border-l border-gray-700 pl-3">
                    {navSection.subsections.map((sub) => (
                      <li key={sub.id}>
                        <button
                          type="button"
                          onClick={() => navigateTo(navSection.id, sub.id)}
                          className={`text-left text-xs ${
                            activeSubsection === sub.id
                              ? 'text-purple-300'
                              : 'text-gray-400 hover:text-gray-200'
                          }`}
                        >
                          {sub.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </nav>

          <div className="mt-6 border-t border-gray-700 pt-4">
            <OfflineCachePanel />
          </div>
        </aside>

        <main>
          {!activeSection && (
            <p className={`mb-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('welcome.description')}
            </p>
          )}
          <ContentViewer />
        </main>
      </div>
    </div>
  );
};

export default AtlasShell;
